import React, { createContext, useContext, useState, useCallback } from 'react';
import type { NotificationModel } from './types';
import { NotificationContainer } from './NotificationContainer';
import { Prompt } from '../components/Prompt';
import type { StatusIntents } from '~/theme/variants';

interface NotificationContextType {
	displayToastNotification: (message: string, messageContext: StatusIntents) => void;
	displayNotificationDialog: (message: string, messageContext: StatusIntents, onConfirm?: () => void) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
	const [notifications, setNotifications] = useState<NotificationModel[]>([]);
	const [notificationDialog, setNotificationDialog] = useState<(NotificationModel & { onConfirm?: () => void }) | null>(null);

	const displayToastNotification = useCallback((message: string, messageContext: StatusIntents = 'success') => {
		const newNotification: NotificationModel = {
			id: Date.now(),
			message,
			context: messageContext
		};

		setNotifications(prev => [...prev, newNotification]);
		setTimeout(() => {
			setNotifications(prev => prev.filter(toast => toast.id !== newNotification.id));
		}, 3000);
	}, []);

	const displayNotificationDialog = useCallback((message: string, messageContext: StatusIntents = 'success', onConfirm?: () => void) => {
		setNotificationDialog({
			id: Date.now(),
			message,
			context: messageContext,
			onConfirm
		});
	}, []);

	const closeNotificationDialog = () => {
		const onConfirm = notificationDialog?.onConfirm;
		if (onConfirm) {
			onConfirm();
		}
		setNotificationDialog(null);
	};

	if (!notifications) {
		return (
			<NotificationContext.Provider value={{ displayToastNotification, displayNotificationDialog }}>{children}</NotificationContext.Provider>
		);
	}

	return (
		<NotificationContext.Provider value={{ displayToastNotification, displayNotificationDialog }}>
			{children}
			<NotificationContainer notifications={notifications} />
			{notificationDialog && (
				<Prompt
					title="Unsaved changes"
					text="Are you sure you want to leave this page? You have unsaved changes that will be lost."
					onAction={closeNotificationDialog}
				/>
			)}
		</NotificationContext.Provider>
	);
}

export function useNotification() {
	const context = useContext(NotificationContext);
	if (!context) {
		throw new Error('useNotification must be used within a NotificationProvider');
	}
	return context;
}
